<template>
  <div>
    <div class="mb-1">
      <v-breadcrumbs
        class="px-0"
        :items="[{ title: '園児・保護者情報', href: '/children', color: 'primary', disabled: false },
        {title: `${child?.lastName } ${ child?.firstName }`,disabled: true}]"
      >
      </v-breadcrumbs>
    </div>
    <div v-if="child">
      <div class="mb-8 d-flex align-center">
        <h2>{{ child.lastName }} {{ child.firstName }}</h2>
        <v-spacer></v-spacer>
        <div>
          <v-btn
            color="primary"
            class="mr-4"
            variant="outlined"
            @click="invitationDownload"
            :disabled="isDownloading || loading"
          >
            <v-icon start>mdi-tray-arrow-down</v-icon>
            案内状ダウンロード
          </v-btn>
          <v-btn
            @click="deleteChild"
            :disabled="loading"
            class="mr-4"
            color="error"
            variant="flat"
          >
            削除
          </v-btn>
          <v-btn
            :disabled="loading"
            color="primary"
            variant="flat"
            :to="`/children/account/${child.childId}/edit`"
            >編集</v-btn
          >
        </div>
      </div>

      <div>
        <v-progress-linear
          v-if="isDownloading"
          indeterminate
          color="primary"
          class="my-8"
        ></v-progress-linear>
      </div>

      <div class="my-8">
        <v-img width="200px" :src="child.imageUrl"></v-img>
      </div>

      <h3>{{ `園児情報` }}</h3>

      <v-table class="mt-4">
        <template v-slot:default>
          <tbody>
            <tr>
              <td class="font-weight-bold">園児区分</td>
              <td>{{ childType }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">ログインID</td>
              <td>{{ child.childCode }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">初回パスワード</td>
              <td>{{ child.password }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">園児コード</td>
              <td>
                <span v-if="childType.optionalChildCode1">
                  {{ childType.optionalChildCode1 }},
                </span>
                <span v-if="childType.optionalChildCode2">
                  {{ childType.optionalChildCode2 }}
                </span>
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold">生年月日</td>
              <td>{{ child.dateOfBirth }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">性別</td>
              <td>{{ child.gender == "male" ? "男性" : "女性" }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">入園日 (予定)</td>
              <td>{{ child.admissionDate }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">卒園日 (予定)</td>
              <td>{{ child.graduationDate }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">退園・転園日</td>
              <td>{{ child.quitDate }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">クラス</td>
              <td>{{ classNames }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">グループ</td>
              <td>{{ groupNames }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">延長保育パターン</td>
              <td>
                {{ child.hoursPattern ? child.hoursPattern.patternTitle : "" }}
              </td>
            </tr>
            <tr>
              <td class="font-weight-bold">配慮が必要な園児への保育者加配</td>
              <td>{{ child.additionalStaffNum }}</td>
            </tr>
            <tr>
              <td class="font-weight-bold">補助金の上限額</td>
              <td>{{ child.maxSubsidyAmount }}</td>
            </tr>
            <!--
            <tr>
              <td class="font-weight-bold">登園・降園のデフォルト設定</td>
              <td>
                {{ timeIntToHourMin(child.defaultStartMin) }} -
                {{ timeIntToHourMin(child.defaultEndMin) }}
              </td>
            </tr>
            -->
          </tbody>
        </template>
      </v-table>

      <v-divider />

      <!-- auth methods -->
      <h3 class="mt-6">ログイン情報</h3>

      <v-table class="mt-4">
        <template v-slot:default>
          <thead>
            <tr>
              <th>氏名</th>
              <th>続柄</th>
              <th>ログイン方法</th>
              <th>登録メールアドレス</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in authMethodsResponse" :key="i">
              <td>
                {{
                  `${item.parentUser.lastName || ""} ${
                    item.parentUser.firstName || ""
                  }`
                }}
              </td>
              <td>{{ getParentTypeText(item.parentUser.parentType) }}</td>
              <td>
                {{
                  item.method == "auth0"
                    ? "メールアドレス・パスワード"
                    : item.method
                }}
              </td>
              <td>{{ item.method == "apple" ? "" : `${item.parentUser.email || ""}` }}</td>
            </tr>
          </tbody>
        </template>
      </v-table>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import { childAccountModule } from "@/store/dataModules/childAccountModule";
import ApiChildren from "@/api/ApiChildren";
import { parentTypeItems } from "@/constants/children";

export default defineComponent({
  async mounted() {
    const childId = this.$route.params.childId as string;
    if (childId) {
      const parsed = parseInt(childId);
      childAccountModule.setChildId(parsed);
      await childAccountModule.getChild();
    }

    await this.getAuthMethods();
  },

  data() {
    return {
      isDownloading: false,
      authMethodsResponse: [],
      parentTypeItems: parentTypeItems,
      loading: false,
    };
  },

  computed: {
    child() {
      return childAccountModule.child;
    },
    childType(): string {
      if (this.child) {
        if (this.child.childcareType === "normal") {
          return "一般保育";
        } else if (this.child.childcareType === "temporary") {
          return "一時保育";
        }
      }
      return "";
    },
    classNames(): string {
      if (this.child) {
        return this.child.nurseryClasses
          .map((nc) => `${nc.year}年度 ${nc.className}`)
          .join(", ");
      }
      return "";
    },
    groupNames(): string {
      if (this.child) {
        return this.child.nurseryGroups.map((g) => g.name).join(", ");
      }
      return "";
    },
  },

  methods: {
    async invitationDownload() {
      this.isDownloading = true;
      const link = await childAccountModule.getInvitationLink();
      this.isDownloading = false;
      if (link) {
        window.open(link, "_blank");
      }
    },
    async getAuthMethods() {
      if (this.child && this.child.childId) {
        const response = await ApiChildren.getAuthMethods({
          childId: this.child.childId,
        });
        if (response) {
          this.authMethodsResponse = response.authMethods;
        }
      }
    },
    async deleteChild() {
      const alertText =
        "\
注意！\n\
この操作は、園児情報をすべて削除します。\n\
今まで蓄積したデータを一回の操作で丸ごと失う危険がありますので、\n\
操作の間違いには、十分にご注意ください。\n\n\
〔 園児情報削除時の注意点 〕 \n\
• 操作は取消できません。\n\
• 現在の園児情報は、この操作によりすべて消去されます。卒園もしくは退園の場合は「卒園日（予定）」を入力して登録してください。\n\
• それでも園児を削除する場合は、下記の削除ボタンを押下してください。\n\
      ";
      const answer = confirm(alertText);
      if (answer) {
        if (this.child && this.child.childId) {
          this.loading = true;
          const response = await ApiChildren.deleteChild({
            childId: this.child.childId,
          }).catch(() => (this.loading = false));
          if (response) {
            alert("削除しました");
            this.$router.push({ path: "/children" });
          }
          this.loading = false;
        }
      }
    },
    getParentTypeText(value: string): string {
      if (!value) {
        return "";
      }
      return parentTypeItems.find((p) => p.value == value)?.text || "";
    },
    timeIntToHourMin(timeInt: number) {
      const hour = Math.floor(timeInt / 60);
      const min = Math.floor(timeInt % 60);
      const minStr = min === 0 ? "00" : String(min);
      return `${hour}:${minStr}`;
    },
  },
});
</script>
